import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  User, 
  Building, 
  MapPin, 
  FileText, 
  Grid3X3, 
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  Save,
  Upload,
  X,
  AlertCircle
} from 'lucide-react';
import api, { sellerAPI } from '../services/api';
import toast from 'react-hot-toast';

// Import step components
import BasicInfoStep from '../components/registration/BasicInfoStep';
import AddressStep from '../components/registration/AddressStep';
import DocumentsStep from '../components/registration/DocumentsStep';
import CategoriesStep from '../components/registration/CategoriesStep';

const SellerRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  
  // Get verified phone number from OTP verification
  const verifiedPhone = location.state?.verifiedPhone || '';
  const tempToken = location.state?.tempToken || '';
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1: Basic Info (Essential fields only)
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: verifiedPhone,
    businessName: '',
    businessType: '',
    gstNumber: '',
    panNumber: '',
    
    // Step 2: Address (Essential only)
    registeredAddress: {
      street: '',
      landmark: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    
    // Step 3: Documents
    documents: {
      gstCertificate: null,
      panCard: null,
      aadharCard: null,
      cancelledCheque: null,
      businessLicense: null,
      tradeLicense: null
    },
    
    // Step 4: Categories
    selectedCategories: []
  });

  const [errors, setErrors] = useState({});
  const [sellerId, setSellerId] = useState(null);
  const [token, setToken] = useState(null);

  const steps = [
    {
      id: 1,
      title: 'Basic Information',
      description: 'Personal and business details',
      icon: User,
      component: BasicInfoStep
    },
    {
      id: 2,
      title: 'Business Address',
      description: 'Registered business address',
      icon: MapPin,
      component: AddressStep
    },
    {
      id: 3,
      title: 'Documents (Optional)',
      description: 'Upload documents or skip for later',
      icon: FileText,
      component: DocumentsStep
    },
    {
      id: 4,
      title: 'Categories',
      description: 'Select your business categories',
      icon: Grid3X3,
      component: CategoriesStep
    }
  ];

  // Load saved progress on component mount
  useEffect(() => {
    // Redirect to phone input if no verified phone number
    if (!verifiedPhone) {
      navigate('/seller-phone-input', { replace: true });
      return;
    }
    
    loadSavedProgress();
  }, [verifiedPhone, navigate]);

  const loadSavedProgress = () => {
    const savedData = localStorage.getItem('sellerRegistrationData');
    const savedStep = localStorage.getItem('sellerRegistrationStep');
    const savedSellerId = localStorage.getItem('sellerId');
    const savedToken = localStorage.getItem('sellerToken');

    if (savedData) {
      setFormData(JSON.parse(savedData));
    }
    if (savedStep) {
      setCurrentStep(parseInt(savedStep));
    }
    if (savedSellerId) {
      setSellerId(savedSellerId);
    }
    if (savedToken) {
      setToken(savedToken);
    }
  };

  const saveProgress = () => {
    localStorage.setItem('sellerRegistrationData', JSON.stringify(formData));
    localStorage.setItem('sellerRegistrationStep', currentStep.toString());
    if (sellerId) {
      localStorage.setItem('sellerId', sellerId);
    }
    if (token) {
      localStorage.setItem('sellerToken', token);
    }
  };

  const updateFormData = (stepData) => {
    setFormData(prev => ({ ...prev, ...stepData }));
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!formData.fullName) newErrors.fullName = 'Full name is required';
        if (!formData.email) newErrors.email = 'Email is required';
        if (!formData.password) newErrors.password = 'Password is required';
        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        }
        if (!formData.phone) newErrors.phone = 'Phone number is required';
        if (!formData.businessName) newErrors.businessName = 'Business name is required';
        if (!formData.businessType) newErrors.businessType = 'Business type is required';
        if (!formData.gstNumber) newErrors.gstNumber = 'GST number is required';
        if (!formData.panNumber) newErrors.panNumber = 'PAN number is required';
        break;
        
      case 2:
        if (!formData.registeredAddress.street) newErrors['registeredAddress.street'] = 'Street address is required';
        if (!formData.registeredAddress.city) newErrors['registeredAddress.city'] = 'City is required';
        if (!formData.registeredAddress.state) newErrors['registeredAddress.state'] = 'State is required';
        if (!formData.registeredAddress.pincode) newErrors['registeredAddress.pincode'] = 'Pincode is required';
        break;
        
      case 3:
        // Documents are now optional - no validation required
        // Users can skip this step and upload documents later from profile
        break;
        
      case 4:
        if (formData.selectedCategories.length === 0) {
          newErrors.selectedCategories = 'Please select at least one category';
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) {
      toast.error('Please fix the errors before proceeding');
      return;
    }

    setLoading(true);
    
    try {
      let response;
      
      switch (currentStep) {
        case 1:
          response = await sellerAPI.registerStep1({
            fullName: formData.fullName,
            email: formData.email,
            password: formData.password,
            phone: formData.phone,
            businessName: formData.businessName,
            businessType: formData.businessType,
            gstNumber: formData.gstNumber,
            panNumber: formData.panNumber
          });
          
          if (response.data.success) {
            setSellerId(response.data.data.sellerId);
            setToken(response.data.token);
            // Set token for future requests
            localStorage.setItem('token', response.data.token);
            // Also set the token in the API instance immediately
            api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
          }
          break;
          
        case 2:
          response = await sellerAPI.registerStep2({
            registeredAddress: formData.registeredAddress
          });
          break;
          
        case 3:
          // Handle document upload (optional)
          const formDataObj = new FormData();
          let hasDocuments = false;
          
          Object.keys(formData.documents).forEach(key => {
            if (formData.documents[key]) {
              formDataObj.append(key, formData.documents[key]);
              hasDocuments = true;
            }
          });
          
          // If no documents uploaded, just proceed to next step
          if (!hasDocuments) {
            response = await sellerAPI.registerStep3({});
          } else {
            response = await sellerAPI.registerStep3(formDataObj);
          }
          break;
          
        case 4:
          // First select categories
          response = await sellerAPI.registerStep4({
            selectedCategories: formData.selectedCategories
          });
          
          if (response.data.success) {
            // Then complete registration
            const completeResponse = await sellerAPI.completeRegistration();
            
            if (completeResponse.data.success) {
              // Clear saved progress
              localStorage.removeItem('sellerRegistrationData');
              localStorage.removeItem('sellerRegistrationStep');
              localStorage.removeItem('sellerId');
              localStorage.removeItem('sellerToken');
              
              toast.success('Registration completed successfully!');
              navigate('/registration-success');
              return;
            }
          }
          break;
      }
      
      if (response?.data?.success) {
        setCurrentStep(prev => prev + 1);
        saveProgress();
        toast.success(`Step ${currentStep} completed successfully!`);
      }
      
    } catch (error) {
      console.error('Step submission error:', error);
      toast.error(error.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      saveProgress();
    }
  };

  const handleSaveProgress = async () => {
    try {
      await sellerAPI.saveProgress({
        step: currentStep,
        data: formData
      });
      saveProgress();
      toast.success('Progress saved successfully!');
    } catch (error) {
      toast.error('Failed to save progress');
    }
  };

  const CurrentStepComponent = steps[currentStep - 1]?.component;

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Seller Registration</h1>
              <p className="text-gray-600 mt-1">Complete your registration to start selling</p>
            </div>
            <button
              onClick={handleSaveProgress}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 flex-shrink-0"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Progress
            </button>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 py-8 overflow-x-hidden">
      <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2">
        <div className="flex items-center min-w-max mx-auto">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            
            return (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`
                    w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mb-2 transition-colors
                    ${isActive ? 'bg-blue-600 text-white' : 
                      isCompleted ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'}
                  `}>
                    {isCompleted ? (
                      <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                    ) : (
                      <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                    )}
                  </div>
                  <div className="text-center">
                    <p className={`text-xs sm:text-sm font-medium ${
                      isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      Step {step.id}
                    </p>
                    <p className="text-xs text-gray-500 mt-1 hidden sm:block">{step.title}</p>
                  </div>
                </div>
                
                {index < steps.length - 1 && (
                  <div className={`
                    w-8 sm:w-16 h-0.5 mx-2 sm:mx-4 transition-colors
                    ${isCompleted ? 'bg-green-500' : 'bg-gray-300'}
                  `} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        {CurrentStepComponent && (
          <CurrentStepComponent
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
            setErrors={setErrors}
          />
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8">
        <button
          onClick={handlePrevious}
          disabled={currentStep === 1}
          className={`
            flex items-center px-6 py-3 rounded-lg font-medium transition-colors
            ${currentStep === 1 
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }
          `}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Previous
        </button>

        <div className="text-sm text-gray-500">
          Step {currentStep} of {steps.length}
        </div>

        <button
          onClick={handleNext}
          disabled={loading}
          className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Processing...
            </>
          ) : currentStep === 4 ? (
            <>
              Complete Registration
              <CheckCircle className="w-4 h-4 ml-2" />
            </>
          ) : (
            <>
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </button>
      </div>
    </div>
    </div>
  );
};

export default SellerRegistration;
